import { useCallback, useMemo, useSyncExternalStore } from "react";
import { doc, updateDoc, deleteField } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../components/AuthProvider";
import { showAlert } from "../alertService";

// Orden default de la sección principal del sidebar. Los ids son estables y
// NUNCA se renombran (el label sí puede cambiar — "venues" se muestra como
// "Venue"): son lo único que se persiste en users/{uid}.sidebarOrder.
// "venues" salió de aquí cuando la ficha se mudó a Configuración: el merge
// tolerante de abajo ignora los ids guardados que ya no existen, así que un
// sidebarOrder viejo con "venues" sigue funcionando.
export const SIDEBAR_DEFAULT_ORDER = [
  "inicio",
  "calendario",
  "leads",
  "proveedores",
  "recursos",
  "eventos",
  "cuentas",
  "reportes",
  "bitacora",
  "config",
];

// Merge tolerante: parte del orden guardado (filtrado a ids que aún existen)
// e inserta cualquier id nuevo en su posición default. Así una sección nueva
// aparece aunque el usuario haya guardado su orden hace un año, y un id
// retirado simplemente se ignora.
export function mergeSidebarOrder(saved) {
  const known = new Set(SIDEBAR_DEFAULT_ORDER);
  const merged = (Array.isArray(saved) ? saved : []).filter((id) =>
    known.has(id)
  );
  SIDEBAR_DEFAULT_ORDER.forEach((id, i) => {
    if (!merged.includes(id)) merged.splice(Math.min(i, merged.length), 0, id);
  });
  return merged;
}

// Reordena solo el subconjunto visible dentro del orden completo: los ids que
// el rol no ve (viewer no ve venues/cuentas/etc.) conservan su posición. Así
// el array guardado siempre es completo y sirve igual para cualquier rol.
export function applyVisibleOrder(fullOrder, newVisibleOrder) {
  const visible = new Set(newVisibleOrder);
  let i = 0;
  return fullOrder.map((id) => (visible.has(id) ? newVisibleOrder[i++] : id));
}

// Store mínimo a nivel de módulo para el override de sesión. A propósito NO
// se guarda vía setUserInfo: VenueProvider (y compañía) dependen de la
// identidad de userInfo, y mutarlo en cada drag rebindeaba todas las
// suscripciones — la "recarga completa" que se veía al soltar. Aquí solo
// re-renderizan los suscriptores del hook (las instancias del sidebar).
// `undefined` = sin override (usa userInfo); `null` = restablecido a default.
let overrideOrder;
const listeners = new Set();

function setOverride(value) {
  overrideOrder = value;
  listeners.forEach((cb) => cb());
}

function subscribe(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

// Mantiene coherente el caché de AuthProvider (localStorage "userInfo") sin
// tocar el estado de React. Al siguiente arranque el sidebar sale con el
// orden correcto aun antes del getDoc.
function patchCachedUserInfo(sidebarOrder) {
  try {
    const raw = localStorage.getItem("userInfo");
    if (!raw) return;
    const cached = JSON.parse(raw);
    if (sidebarOrder === null) delete cached.sidebarOrder;
    else cached.sidebarOrder = sidebarOrder;
    localStorage.setItem("userInfo", JSON.stringify(cached));
  } catch {
    // Caché ilegible: el getDoc del próximo login lo repara.
  }
}

// El orden vive en users/{uid}.sidebarOrder (cross-device; el user doc ya se
// lee al entrar). En la sesión activa manda el override del store.
export function useSidebarOrder() {
  const { user, userInfo } = useAuth();
  const override = useSyncExternalStore(subscribe, () => overrideOrder);

  const saved = override === undefined ? userInfo?.sidebarOrder : override;
  const order = useMemo(() => mergeSidebarOrder(saved), [saved]);
  const isCustomized = Array.isArray(saved) && saved.length > 0;

  const save = useCallback(
    async (newOrder) => {
      if (!user?.uid) return;
      setOverride(newOrder);
      patchCachedUserInfo(newOrder);
      try {
        await updateDoc(doc(db, "users", user.uid), { sidebarOrder: newOrder });
      } catch (error) {
        console.error("No se pudo guardar sidebarOrder:", error);
        showAlert("No se pudo guardar el orden del menú", "error");
      }
    },
    [user?.uid]
  );

  // Borra el campo en lugar de guardar el default: si el default cambia en
  // el futuro, quien nunca personalizó lo recibe automáticamente.
  const reset = useCallback(async () => {
    if (!user?.uid) return;
    setOverride(null);
    patchCachedUserInfo(null);
    try {
      await updateDoc(doc(db, "users", user.uid), {
        sidebarOrder: deleteField(),
      });
    } catch (error) {
      console.error("No se pudo restablecer sidebarOrder:", error);
      showAlert("No se pudo restablecer el orden del menú", "error");
    }
  }, [user?.uid]);

  return { order, save, reset, isCustomized };
}
